import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ROUTER_HISTORY } from 'app/AppRouter';
import { ChevronRightIcon, SearchIcon } from 'assets/icons';
import { getLastSearchedVehicleKey } from 'domains/catalog/Catalog.store';
import { getVehicleSearchHistory } from 'domains/user';
import { CenteredSpin, Text } from 'UI';
import { hasData, isLoading } from 'utils';
import { SSearchBarWrapper, SSearchHistoryContent, SSearchInput, SViewAllRow } from './SearchHistoryContent.styled';
import { SearchHistoryRow, getVehicleName } from './SearchHistoryRow';

export default function SearchHistoryContent() {
  const { t } = useTranslation();
  const history = useHistory();
  const vehicleSearchHistory = useSelector(getVehicleSearchHistory);
  const lastSearchedVehicleKey = useSelector(getLastSearchedVehicleKey);
  const [filterText, setFilterText] = useState('');

  const handleFilterChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterText(e.target.value);
  }, []);

  const handleViewAll = useCallback(() => {
    history.push(ROUTER_HISTORY);
  }, [history]);

  const handleViewAllKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        history.push(ROUTER_HISTORY);
      }
    },
    [history],
  );

  if (!hasData(vehicleSearchHistory.searchStatus) || (vehicleSearchHistory.data?.length ?? 0) < 1) {
    return (
      <SSearchHistoryContent>
        {isLoading(vehicleSearchHistory.searchStatus) ? (
          <CenteredSpin />
        ) : (
          <Text type="base" bold>
            {t('catalog.history.is_empty', 'Your search history is empty')}
          </Text>
        )}
      </SSearchHistoryContent>
    );
  }

  const vehicles = vehicleSearchHistory.data ?? [];

  const filteredVehicles =
    filterText.trim() === ''
      ? vehicles
      : vehicles.filter((v) => {
          const name = getVehicleName(v).toLowerCase();
          const vrn = (v.vrn ?? '').toLowerCase();
          const vin = (v.vin ?? '').toLowerCase();
          const query = filterText.toLowerCase();
          return name.includes(query) || vrn.includes(query) || vin.includes(query);
        });

  return (
    <>
      <SSearchBarWrapper>
        <SearchIcon />
        <SSearchInput
          placeholder={t('catalog.history.search_placeholder', 'Search in car history')}
          value={filterText}
          onChange={handleFilterChange}
        />
      </SSearchBarWrapper>
      <SSearchHistoryContent>
        {filteredVehicles.length === 0 ? (
          <Text type="base" bold>
            {t('catalog.history.no_results', 'No matching vehicles')}
          </Text>
        ) : (
          filteredVehicles.map((vehicle) => (
            <SearchHistoryRow
              key={vehicle.vehicleKey}
              vehicle={vehicle}
              isActive={vehicle.vehicleKey === lastSearchedVehicleKey}
            />
          ))
        )}
      </SSearchHistoryContent>
      <SViewAllRow
        onClick={handleViewAll}
        onKeyDown={handleViewAllKeyDown}
        role={'button'}
        tabIndex={0}
        data-testid={'view-all-history'}
      >
        <Text type="base" bold>
          {t('catalog.history.view_all', 'View all history')}
        </Text>
        <ChevronRightIcon />
      </SViewAllRow>
    </>
  );
}
